import type { UploadPhase, UploadTask } from '../types/uploads';

export type UploadPipeline =
  | 'submission-video'
  | 'submission-mcap'
  | 'source-import-video'
  | 'source-import-mcap';

const TERMINAL_AND_ATTENTION: readonly UploadPhase[] = [
  'accepted',
  'blocked-duplicate',
  'blocked-near-duplicate',
  'rejected-duplicate',
  'rejected-quality',
  'failed',
  'error',
  'cancelled',
];

const PIPELINE_PHASES: Record<UploadPipeline, ReadonlySet<UploadPhase>> = {
  'submission-video': new Set<UploadPhase>([
    'queued',
    'reattaching',
    'hashing',
    'prechecking',
    'creating',
    'uploading',
    'paused',
    'transferring',
    'completing',
    'processing',
    ...TERMINAL_AND_ATTENTION,
  ]),
  'submission-mcap': new Set<UploadPhase>([
    'queued',
    'reattaching',
    'hashing',
    'creating',
    'uploading',
    'paused',
    'completing',
    'processing',
    ...TERMINAL_AND_ATTENTION,
  ]),
  'source-import-video': new Set<UploadPhase>([
    'queued',
    'creating',
    'prechecking',
    'transferring',
    'processing',
    ...TERMINAL_AND_ATTENTION,
  ]),
  'source-import-mcap': new Set<UploadPhase>([
    'queued',
    'creating',
    'transferring',
    'processing',
    ...TERMINAL_AND_ATTENTION,
  ]),
};

export function uploadPipeline(item: UploadTask): UploadPipeline {
  if (item.channel === 'upload') {
    return item.mediaType === 'mcap' ? 'submission-mcap' : 'submission-video';
  }
  return item.mediaType === 'mcap' ? 'source-import-mcap' : 'source-import-video';
}

export function isPhaseSupportedByPipeline(pipeline: UploadPipeline, phase: UploadPhase): boolean {
  return PIPELINE_PHASES[pipeline].has(phase);
}

/**
 * Central reducer for every live UploadTask patch. Producers still own facts (upload runtime or
 * backend adapters), while this machine enforces cross-cutting pipeline invariants before the UI,
 * persistence layer and SharedWorker receive the next snapshot.
 */
export function transitionUploadTask(current: UploadTask, patch: Partial<UploadTask>): UploadTask {
  let next: UploadTask = { ...current, ...patch };
  const pipeline = uploadPipeline(next);

  if (!isPhaseSupportedByPipeline(pipeline, next.phase)) {
    next = {
      ...next,
      phase: 'error',
      reason: `Unsupported ${next.phase} state for ${pipeline}`,
      statusSyncError: true,
      needsReattach: undefined,
    };
  }

  if (next.channel === 'import') {
    next = {
      ...next,
      needsReattach: undefined,
      resumeAvailable: undefined,
      autoRetry: undefined,
      autoRetryCount: undefined,
    };
  } else if (next.statusSyncError) {
    next = { ...next, needsReattach: undefined };
  }

  return next;
}
